import { ofType } from 'redux-observable';
import { catchError, map, switchMap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import type { RootEpic } from '@/store/types';
import { storageActions } from '@/features/settings/components/StorageManagement/redux/storageSlice';
import { uploadFileActions } from '@/features/upload/stores/uploadFiles/uploadFileSlice';
import { ACCESS_KEY } from '@/api/HttpClient';
import { uploadSwClient } from '@/features/file-management/pages/FilePage/uploadManager';

type SavedConflictPreference = "overwrite" | "createNew" | "cancel" | null;

function getUploadHeaders(project: string, companyId: string, parentFolderId: string, isOrg: boolean): Record<string, string> {
  const token =
    localStorage.getItem(ACCESS_KEY) ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("token") ||
    localStorage.getItem("jwt") ||
    "";
  const auth = token ? (token.startsWith("Bearer ") ? token : `Bearer ${token}`) : "";
  const orgId = localStorage.getItem("orgId") || "";
  return {
    ...(auth && { Authorization: auth }),
    "X-Project-Id": project,
    "X-Company-Id": companyId,
    "X-Parent-Folder-Id": parentFolderId,
    "X-Is-Organization": String(isOrg),
    "X-Org-Id": orgId,
  };
}

function getConflictPolicyHeader(
  preference: SavedConflictPreference
): Record<string, string> {
  if (preference === "overwrite") {
    return { "X-Conflict-Policy": "overwrite" };
  }
  if (preference === "createNew") {
    return { "X-Conflict-Policy": "createNew" };
  }
  return { "X-Conflict-Policy": "prompt" };
}

/**
 * UPLOAD INTERCEPT EPIC
 * 
 * Responsibility: Listen to proceedUpload action and dispatch actual upload actions
 * 
 * Flow:
 * 1. storageCapacityEpic validates capacity
 * 2. If OK → dispatch proceedUpload with uploadContext
 * 3. This epic intercepts proceedUpload
 * 4. Dispatches appropriate upload action based on uploadType
 *    - 'file' → uploadFileActions.checkFileExistsRequest
 *    - 'folder' → folderUploadActions.startFolderUpload
 */
export const uploadInterceptEpic$: RootEpic = (action$, state$) =>
  action$.pipe(
    ofType(storageActions.proceedUpload.type),
    switchMap((action: ReturnType<typeof storageActions.proceedUpload>) => {
      const { uploadContext } = action.payload;
      const { uploadType, uploadPayload } = uploadContext;
      const conflictPreference = state$.value.uploadFile.conflictPreference;
      const client = uploadSwClient;
      if (!client) {
        return of({ type: "UPLOAD_SW_CLIENT_NOT_AVAILABLE" });
      }

      if (uploadType === 'file') {
        const files = uploadPayload.files || [];
        const folderId = uploadPayload.parentFolderId || '';
        const isOrganization = uploadPayload.isOrganization;
        const projectId = uploadPayload.project;
        const companyId = uploadPayload.companyId;
        const tasks = files.map((file: File) => ({
          id: `${file.name}_${folderId}_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          file,
          headers: {
            ...getUploadHeaders(projectId, companyId, folderId, Boolean(isOrganization)),
            ...getConflictPolicyHeader(conflictPreference),
          },
          folderId,
          relativePath: file.webkitRelativePath || "",
          maxRetries: 2,
        }));

        return from(client.enqueue(tasks)).pipe(
          switchMap((ids) => {
            console.log("[uploadInterceptEpic] file enqueue done", { requested: tasks.length, ids: ids.length });
            // Do NOT clear optimistic files here — the panel filters them out naturally once
            // the SW snapshot contains those file names, preventing a blank-panel flash.
            return from([uploadFileActions.setPreparingUpload(false)]);
          }),
          catchError((err) => {
            console.error("[uploadInterceptEpic] file enqueue failed", err);
            return from([uploadFileActions.clearOptimisticFiles(), uploadFileActions.setPreparingUpload(false)]);
          })
        );
      } else if (uploadType === 'folder') {
        const files: File[] = uploadPayload.files || [];
        const relativePaths: string[] = uploadPayload.relativePaths || [];
        const folderId = uploadPayload.parentFolderId || '';
        const isOrganization = uploadPayload.isOrganization;
        const projectId = uploadPayload.projectId || uploadPayload.project || "";
        const companyId = uploadPayload.companyId || localStorage.getItem("companyId") || "";
        const tasks = files.map((file: File, index: number) => ({
          id: `${file.name}_${folderId}_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          file,
          headers: {
            ...getUploadHeaders(projectId, companyId, folderId, Boolean(isOrganization)),
            ...getConflictPolicyHeader(conflictPreference),
          },
          folderId,
          relativePath: relativePaths[index] || file.webkitRelativePath || "",
          maxRetries: 2,
        }));

        return from(client.enqueue(tasks)).pipe(
          switchMap((ids) => {
            console.log("[uploadInterceptEpic] folder enqueue done", { requested: tasks.length, ids: ids.length });
            // Do NOT clear optimistic files here — panel clears them once SW snapshot catches up.
            return from([uploadFileActions.setPreparingUpload(false)]);
          }),
          catchError((err) => {
            console.error("[uploadInterceptEpic] folder enqueue failed", err);
            return from([uploadFileActions.clearOptimisticFiles(), uploadFileActions.setPreparingUpload(false)]);
          })
        );
      }

      // Unknown upload type - do nothing
      console.warn('⚠️ Unknown upload type:', uploadType);
      return from([uploadFileActions.clearOptimisticFiles(), uploadFileActions.setPreparingUpload(false)]);
    })
  );

/** Clear preparing state and optimistic files when storage capacity is exceeded */
export const clearPreparingOnStorageExceededEpic$: RootEpic = (action$) =>
  action$.pipe(
    ofType(storageActions.storageCapacityExceeded.type),
    switchMap(() => from([uploadFileActions.clearOptimisticFiles(), uploadFileActions.setPreparingUpload(false)]))
  );

export const uploadInterceptEpics = [uploadInterceptEpic$, clearPreparingOnStorageExceededEpic$];
